import { MESApiService, type spParameter } from "@dexteel/mesf-core";
import { get } from "lodash-es";
import type { OverviewHeader } from "@/pages/weldlines/models/types";
import type { SOURCES, Values } from "../tagStation";

export type MillBundleState = "Station" | "ToPrint" | "Aside" | "Printed";
export type MillBundleSource = keyof typeof SOURCES;

export interface MillBundle {
	MillBundleId: number;
	TagNo: string | null;
	Source: MillBundleSource;
	// where from, when Source is Other
	SourceNote: string | null;
	IsManual: boolean;
	State: MillBundleState;
	IsScrap: boolean;
	SectionName: string;
	GradeName: string | null;
	HeatNo: string | null;
	LengthIn: number;
	Pieces: number;
	SentSectionName: string;
	SentGradeName: string | null;
	SentHeatNo: string | null;
	SentLengthIn: number;
	SentPieces: number;
	Comment: string | null;
	ArrivedAt: string;
	PrintedAt: string | null;
	PrintedByEmployee: string | null;
	// last print of its tag
	PrintStatus: string | null;
	PrintMessage: string | null;
}

export interface TagStationHeader
	extends Pick<
		OverviewHeader,
		| "CurrShiftId"
		| "CurrShiftStart"
		| "CurrShiftEnd"
		| "CurrShiftLabel"
		| "CurrCrew"
	> {
	RollingSection: string | null;
	LastLengthIn: number | null;
	LastPieces: number | null;
	// the number the next print takes, if nobody prints first
	NextTagNo: string | null;
	PrinterStatus: string | null;
}

export interface TagStationData {
	header?: TagStationHeader;
	bundles: MillBundle[];
	comments: string[];
	sourceNotes: string[];
}

export interface NewMillBundle {
	where: "Station" | "Aside";
	source: MillBundleSource;
	sourceNote: string;
	values: Values;
	comment: string;
}

export interface MillSection {
	SectionName: string;
	LBsPerFoot: number | null;
	Description: string | null;
}

export interface MillHeat {
	HeatNo: string;
	GradeName: string | null;
}

export interface TagStationCatalogs {
	sections: MillSection[];
	grades: string[];
	// newest first
	heats: MillHeat[];
}

const call = async (procedure: string, parameters: spParameter[] = []) => {
	const response = await new MESApiService().callV2(procedure, parameters);
	if (!response.ok)
		throw new Error(response.message || "Unable to reach the Tag Station.");
	return response;
};

export const getTagStation = async (): Promise<TagStationData> => {
	const response = await call("[FAB].[GetTagStation]");
	return {
		header: get(response, "data.tables[0].rows[0]") as
			| TagStationHeader
			| undefined,
		bundles: get(response, "data.tables[1].rows", []) as MillBundle[],
		comments: (
			get(response, "data.tables[2].rows", []) as { Comment: string }[]
		).map((r) => r.Comment),
		sourceNotes: (
			get(response, "data.tables[3].rows", []) as { SourceNote: string }[]
		).map((r) => r.SourceNote),
	};
};

export const getTagStationCatalogs = async (): Promise<TagStationCatalogs> => {
	const response = await call("[FAB].[GetTagStationCatalogs]");
	return {
		sections: get(response, "data.tables[0].rows", []) as MillSection[],
		grades: (
			get(response, "data.tables[1].rows", []) as { GradeName: string }[]
		).map((r) => r.GradeName),
		heats: get(response, "data.tables[2].rows", []) as MillHeat[],
	};
};

// null = heat not in the mill's list
export const getMillHeatGrade = async (heat: string): Promise<string | null> =>
	get(
		await call("[FAB].[GetMillHeatGrade]", [{ name: "HeatNo", value: heat }]),
		"data.tables[0].rows[0].GradeName",
		null,
	) as string | null;

const valueParams = (v: Values): spParameter[] => [
	{ name: "SectionName", value: v.section },
	{ name: "GradeName", value: v.grade || null },
	{ name: "HeatNo", value: v.heat },
	{ name: "LengthIn", value: v.lengthIn },
	{ name: "Pieces", value: v.pieces },
];

export const createMillBundle = (bundle: NewMillBundle, employee: string) =>
	call("[FAB].[CreateMillBundle]", [
		{ name: "Where", value: bundle.where },
		{ name: "Source", value: bundle.source },
		{ name: "SourceNote", value: bundle.sourceNote || null },
		...valueParams(bundle.values),
		{ name: "Comment", value: bundle.comment || null },
		{ name: "EmployeeNumber", value: employee },
	]);

export interface PrintedTag {
	printQueueItemId: number;
}

const printTag = async (parameters: spParameter[]): Promise<PrintedTag> => ({
	printQueueItemId: get(
		await call("[FAB].[PrintMillBundle]", parameters),
		"data.tables[0].rows[0].PrintQueueItemId",
	),
});

export const printMillBundle = (
	millBundleId: number,
	values: Values,
	comment: string,
	employee: string,
) =>
	printTag([
		{ name: "MillBundleId", value: millBundleId },
		...valueParams(values),
		{ name: "Comment", value: comment || null },
		{ name: "EmployeeNumber", value: employee },
		{ name: "Reprint", value: false },
	]);

// the bundle as it is now
export const reprintMillBundle = (millBundleId: number, employee: string) =>
	printTag([
		{ name: "MillBundleId", value: millBundleId },
		{ name: "EmployeeNumber", value: employee },
		{ name: "Reprint", value: true },
	]);

export const deleteMillBundle = (millBundleId: number, employee: string) =>
	call("[FAB].[DeleteMillBundle]", [
		{ name: "MillBundleId", value: millBundleId },
		{ name: "EmployeeNumber", value: employee },
	]);

export const setMillBundleFlags = (
	millBundleId: number,
	flags: { aside?: boolean; scrap?: boolean },
	employee: string,
) =>
	call("[FAB].[SetMillBundleFlags]", [
		{ name: "MillBundleId", value: millBundleId },
		{ name: "Aside", value: flags.aside ?? null },
		{ name: "Scrap", value: flags.scrap ?? null },
		{ name: "EmployeeNumber", value: employee },
	]);
